/**
 * Echo Hunt -- the LLM "roles" a pipeline step can run as (P3, §16.0 "LLM execution").
 *
 * Every LLM call in Echo Hunt names a role, and each role can be pointed at its own model on the
 * Settings tab (stored in `echoProfiles.modelRoles`, falling back to the global model when empty).
 *
 * PURE module: no React, no `store`, no DB imports (see README.md). It is imported by both the pure
 * LLM helper and the `@` DB types, so it must stay dependency-free.
 */

export const LLMRole_values = [
	"indexer",
	"generator",
	"reshape",
	"retention",
	"weaver",
	"rewriter",
	"judge",
	"heldOutJudge",
	/** drafts WORLDS and CHARACTERS on request from the Worlds/Characters tabs (§8.5, §8.7). Never runs
	 * during a round, so it cannot leak anything into the hunt; it exists so the authoring helpers share
	 * one model setting instead of borrowing a pipeline role's. */
	"loreGenerator",
	/** writes the narrated lucid interlude that closes a story when the suspicion meter boils over (§8.4,
	 * P11). Runs once per story, OUTSIDE a round: no bullets exist in its input, so it cannot leak a label
	 * even in principle. */
	"narrator",
] as const;
export type LLMRole = typeof LLMRole_values[number];

/** Human-readable role labels (Settings tab, logs). */
export const LLMRole_labels: Record<LLMRole, string> = {
	indexer: "Indexer",
	generator: "Generator",
	reshape: "Reshape",
	retention: "Retention",
	weaver: "Weaver",
	rewriter: "Rewriter",
	judge: "Judge",
	heldOutJudge: "Held-out judge",
	loreGenerator: "Lore generator",
	narrator: "Narrator",
};

/** Short description of what each role does, shown on the Settings tab. */
export const LLMRole_descriptions: Record<LLMRole, string> = {
	indexer: "corpus index + self-contained rewrite (§4)",
	generator: "fake bullet generation (§5)",
	reshape: "shared reshape with seed words (§6)",
	retention: "does the reshaped bullet still describe the source event? (§6)",
	weaver: "orders + rebridges the bullets into a story (§8.1)",
	rewriter: "in-place repair rewrite of one real bullet (§9.3)",
	judge: "stranger filter in the judge loop (§9)",
	heldOutJudge: "never used by the loop; logs an honest leakage estimate (§9.5)",
	loreGenerator: "drafts a world bible or a character on request (authoring only, never in a round)",
	narrator: "writes the lucid interlude that closes a story (§8.4; once per story, no bullets in the input)",
};

export function IsLLMRole(value: unknown): value is LLMRole {
	return typeof value == "string" && (LLMRole_values as readonly string[]).includes(value);
}

/** Per-role model overrides. An absent/empty entry means "fall back to the global model" (§16.1).
 * This is the shape stored at `echoProfiles.modelRoles`. */
export type RoleModelMap = Partial<Record<LLMRole, string|n>>;

/** Cleans a user-entered model id; returns null when it's empty (meaning "use the global model"). */
export function NormalizeModelOverride(model: string|n): string|n {
	const trimmed = model?.trim();
	return trimmed != null && trimmed.length > 0 ? trimmed : null;
}

/** Reasoning-effort values (2026-10-21). Mirrors the app's existing `EffortLevel` enum
 * (`Server/DB/engineConfigs/@EngineConfig/@EC_StoryExplore.ts`) by IDENTIFIER, but is declared here as a string
 * union rather than imported: this module must stay dependency-free so the pure pipeline modules remain
 * testable in the Node lane, and that enum's module imports `mobx-graphlink`. The `*_Live` layer converts
 * between the two by name (they are string enums with identical values). */
export const ReasoningEffort_values = ["none", "minimal", "low", "medium", "high", "xhigh", "max"] as const;
export type ReasoningEffort = typeof ReasoningEffort_values[number];

/** Per-role REASONING-EFFORT overrides. Needed because some models (eg. `anthropic/claude-opus-5.5`) REFUSE a
 * request that disables reasoning -- `"Reasoning is mandatory for this endpoint and cannot be disabled"` -- while
 * the shared LLM helper defaults thinking to disabled. An absent entry means "auto": a model whose metadata says
 * reasoning is mandatory gets its own default effort, and everything else is left exactly as it was.
 *
 * Stored at `echoProfiles.modelReasoning`. */
export type RoleReasoningMap = Partial<Record<LLMRole, ReasoningEffort|n>>;

/** Which reasoning effort a role has been given, or null for "auto" (see `RoleReasoningMap`). */
export function ResolveReasoningForRole(modelReasoning: RoleReasoningMap|n, role: LLMRole): ReasoningEffort|n {
	return modelReasoning?.[role] ?? null;
}
